import type { ReactNode } from 'react';
import { faHouseUser } from '@fortawesome/pro-duotone-svg-icons';
import { faCircleEllipsis } from '@fortawesome/pro-regular-svg-icons';
import { useHistory } from 'react-router-dom';

// mui
import { Box } from '@mui/material';
import { makeStyles, type DefaultTheme } from '@mui/styles';

// components
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useFeature } from '@growthbook/growthbook-react';
import { useSelector } from 'react-redux';
import { SwitchAccountsMenu } from './helpers';
import { AgencyIdentityBlock } from './modules/AgencyIdentityBlock';
import { AgencyUserAvatarMenu } from './modules/AgencyUserAvatarMenu';

// utils
import { withShowable } from '../../../_global/lib/withShowable';
import { usePopOverState } from '../../../agencies/quotes/PrimeX/customizeQuote/components/DatePicker';

// apis
import { Show } from '../../../../components/Show';
import { useActor } from '../../../../components/hooks/useActor';
import { FEATURE_FLAG_KEYS } from '../../../../config/growthbook';
import { selectNewAgentExperienceLayout } from '../../../../store/selectors/ui-settings.selectors';
import GlobalTeamsMenu from '../../../../teams/GlobalTeamsSelector';
import { AGENCY_DASHBOARD_VIEWS } from '../../../dashboard/agency/agency-dashboard.statics';
import { AgencyDashboardMegaMenu } from './modules/AgencyDashboardMegaMenu';
import LanguageSelector from './modules/LanguageSelector';
import { LinkedLogo } from './modules/LinkedLogo';
import { agencyHeaderWidth } from './agencyHeaderWidth';
import { HEADER_END_INSET } from './HeaderItems';

interface AgencyDashboardHeaderProps {
  /**
   * What ends the header beside the user's avatar: the Ask Moo launcher. It sits inside this header's own width, which
   * the compressed layout caps and centres, so it never drifts to the window's edge away from the avatar.
   */
  readonly trailing?: ReactNode;
  /** Whether the "..." mega menu sits beside Home. The broker experience's left rail replaces it. Defaults to true. */
  readonly megaMenu?: boolean;
  /**
   * Span the whole row instead of the compressed layout's capped, centred box. The broker experience sets it, so the
   * logo sits at the left edge and Ask Moo at the right like the other personas' header. Defaults to false.
   */
  readonly fullWidth?: boolean;
  /** Lead the header's right side with the agency's name and location. The broker experience sets it. Defaults to false. */
  readonly withAgency?: boolean;
  /**
   * The house button beside the logo, which opens the dashboard. The broker experience leaves it out: the logo and the
   * rail's Dashboard already go there. Defaults to true.
   */
  readonly home?: boolean;
}

const AgencyDashboardHeader = withShowable(
  ({
    trailing,
    megaMenu = true,
    fullWidth = false,
    withAgency = false,
    home = true,
  }: AgencyDashboardHeaderProps) => {
    const layout = useSelector(selectNewAgentExperienceLayout);
    const { maxWidth, margin } = agencyHeaderWidth({ layout, fullWidth });

    return (
      <Box
        display="flex"
        width="100%"
        justifyContent="space-between"
        position="relative"
        style={{ maxWidth, margin }}
      >
        <NavigationButtons megaMenu={megaMenu} home={home} />
        <UserPreferences
          trailing={trailing}
          fullWidth={fullWidth}
          withAgency={withAgency}
        />
      </Box>
    );
  }
);

function NavigationButtons({
  megaMenu,
  home,
}: Readonly<{ megaMenu: boolean; home: boolean }>) {
  const history = useHistory();
  const iconClasses = useIconStyles({});

  const handleHomeClick = () => {
    history.push(`/agency/dashboard?view=${AGENCY_DASHBOARD_VIEWS.LISTING}`);
  };

  return (
    <Box display="flex" alignItems="center" sx={{ gap: 3 }}>
      <LinkedLogo />
      {home && (
        <Box className={iconClasses.container}>
          <FontAwesomeIcon
            data-qa="BUTTON:home"
            onClick={handleHomeClick}
            className={iconClasses.root}
            icon={faHouseUser}
            size="2x"
          />
        </Box>
      )}
      {megaMenu && <AgencyOptionsMenu />}
      <SwitchAccountsMenu />
    </Box>
  );
}

function AgencyOptionsMenu() {
  const popoverState = usePopOverState();
  const iconClasses = useIconStyles({ isOpen: popoverState.open });

  return (
    <Box className={iconClasses.container}>
      <FontAwesomeIcon
        onClick={
          popoverState.onOpen as unknown as (
            event: React.MouseEvent<SVGSVGElement>
          ) => void
        }
        icon={faCircleEllipsis}
        style={{ fontSize: '2.15rem' }}
        className={iconClasses.root}
        data-qa="BUTTON:agency-options"
      />
      <AgencyDashboardMegaMenu
        open={popoverState.open}
        anchorEl={popoverState.anchorEl}
        onClose={popoverState.onClose}
      />
    </Box>
  );
}

function UserPreferences({
  trailing,
  fullWidth,
  withAgency,
}: Readonly<
  Pick<AgencyDashboardHeaderProps, 'trailing' | 'fullWidth' | 'withAgency'>
>) {
  const languageSelectorFeature = useFeature(
    FEATURE_FLAG_KEYS.LANGUAGE_SELECTOR
  );
  const actor = useActor();

  return (
    <Box
      display="flex"
      alignItems="center"
      gap="2rem"
      mr={fullWidth ? HEADER_END_INSET : '0.6rem'}
    >
      {withAgency && <AgencyIdentityBlock />}

      <GlobalTeamsMenu />

      <AgencyUserAvatarMenu />

      <Show when={languageSelectorFeature.on && actor.isCowbell}>
        <LanguageSelector />
      </Show>

      {trailing}
    </Box>
  );
}

const useIconStyles = makeStyles<DefaultTheme, { isOpen?: boolean }>(
  (theme) => ({
    root: {
      color: theme.agencyDash.icon,
    },
    container: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: '3rem',
      height: '3rem',
      padding: '0.2rem',

      boxShadow: ({ isOpen }) =>
        isOpen ? '2px 2px 5px 0 rgba(0, 0, 0, 0.1)' : 'none',
      cursor: 'pointer',
      borderRadius: 5,
      '&:hover': {
        scale: '1.05',
        boxShadow: '2px 2px 5px 0 rgba(0, 0, 0, 0.1)',
      },
      transition: 'scale 100ms, box-shadow 100ms',
    },
  })
);

export default AgencyDashboardHeader;
